import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowDown, Shield, TrendingUp, Globe, Clock, Calendar, Users, Eye, ShieldCheck, Landmark, Building, CheckCircle, Lock, Unlock, Quote } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PartnerModal from '../components/PartnerModal';
import InvestmentProposalModal from '../components/InvestmentProposalModal';
import { MotionReveal } from '../components/strategy/Motion';
import {
  companyData, aboutSection, investmentFocus, capitalFramework, investmentStructure,
  strategicRole, governanceSection, capitalNetwork, whyPartner, closingSection,
} from '../data/mock';

const FOCUS_ICONS = { shield: Shield, 'trending-up': TrendingUp, globe: Globe };
const ROLE_ICONS = { clock: Clock, calendar: Calendar, growth: TrendingUp };
const GOV_ICONS = { users: Users, eye: Eye, 'shield-check': ShieldCheck };
const NETWORK_ICONS = { landmark: Landmark, building: Building };
const CLOSING_ICONS = { 'check-circle': CheckCircle, lock: Lock, unlock: Unlock };

const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase text-[#2f9e8e]">
    <span className="w-8 h-px bg-[#2f9e8e]/60" /> {children}
  </div>
);

const InstitutionalPage = () => {
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const [isProposalModalOpen, setIsProposalModalOpen] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handlePartnerClick = () => setIsPartnerModalOpen(true);
  const handleLearnMore = () => {
    const aboutEl = document.getElementById('about');
    if (aboutEl) aboutEl.scrollIntoView({ behavior: 'smooth' });
  };
  const handleStartConversation = () => {
    const subject = encodeURIComponent('Starting a Conversation - Berkshire Investment Group');
    const body = encodeURIComponent('Hello,\n\nI would like to start a conversation about potential partnership opportunities.\n\nBest regards');
    window.location.href = `mailto:${companyData.contactEmail}?subject=${subject}&body=${body}`;
  };
  const handleSubmitProposal = () => setIsProposalModalOpen(true);

  const heroStats = [...aboutSection.stats, ...capitalFramework.stats];

  return (
    <div className="App font-sans bg-white text-[#1a365d] min-h-screen">
      <Header onPartnerClick={handlePartnerClick} />

      {/* HERO */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden bg-gradient-to-br from-white via-[#f6f9fb] to-white">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'linear-gradient(#2f9e8e 1px, transparent 1px), linear-gradient(90deg, #2f9e8e 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }} />
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#2f9e8e]/12 blur-3xl pointer-events-none" style={{ animation: 'big-pulse 8s ease-in-out infinite' }} />
        <div className="absolute -bottom-32 -left-32 w-[600px] h-[600px] rounded-full bg-[#c19a3c]/12 blur-3xl pointer-events-none" style={{ animation: 'big-pulse 10s ease-in-out infinite 2s' }} />

        <div className="relative max-w-7xl mx-auto">
          <Eyebrow>Institutional Capital Partnership</Eyebrow>
          <h1 className="font-serif text-5xl md:text-7xl font-bold leading-[1.05] tracking-tight mt-6 mb-8 max-w-4xl text-[#1a365d]">
            The capital partner <span className="text-[#2f9e8e]">GPs would design</span> themselves.
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 max-w-3xl leading-relaxed mb-12">
            {companyData.tagline}
          </p>

          <div className="flex flex-wrap gap-3 mb-16">
            <button onClick={handlePartnerClick}
              className="group inline-flex items-center gap-2 bg-[#2f9e8e] hover:bg-[#268578] text-white px-7 py-4 rounded-lg font-semibold transition-all hover:shadow-xl hover:-translate-y-0.5"
              data-testid="institutional-cta-partner">
              Partner With Us <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={handleLearnMore}
              className="group inline-flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-300 hover:border-[#1a365d] text-[#1a365d] px-7 py-4 rounded-lg font-semibold transition-all"
              data-testid="institutional-cta-learn-more">
              Learn More <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
            </button>
          </div>

          {/* Headline stats strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 rounded-lg overflow-hidden border border-gray-200">
            {heroStats.map((s, i) => (
              <MotionReveal key={s.label} delay={i * 80} className="bg-white p-5">
                <div className="text-2xl md:text-3xl font-bold text-[#1a365d]">{s.value}</div>
                <div className="text-xs text-gray-600 mt-1 leading-tight">{s.label}</div>
                <div className="text-[10px] text-gray-400 mt-0.5">{s.sublabel}</div>
              </MotionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-24 px-6 bg-[#f6f9fb]">
        <div className="max-w-7xl mx-auto">
          <Eyebrow>About BIG</Eyebrow>
          <h2 className="font-serif text-3xl md:text-5xl font-bold leading-tight mt-5 mb-10 max-w-3xl text-[#1a365d]">
            {aboutSection.title}
          </h2>
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-5">
              {aboutSection.description.map((para, index) => (
                <p key={index} className="text-lg text-gray-700 leading-relaxed">{para}</p>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4 content-start">
              {aboutSection.stats.map((stat, index) => (
                <MotionReveal key={stat.label} delay={index * 100}>
                  <div className="bg-white border border-gray-200 rounded-xl p-7 hover:border-[#2f9e8e]/40 hover:shadow-lg transition-all h-full">
                    <div className="text-4xl font-bold text-[#2f9e8e] mb-2">{stat.value}</div>
                    <div className="font-serif text-lg font-bold text-[#1a365d] mb-1">{stat.label}</div>
                    <div className="text-sm text-gray-500">{stat.sublabel}</div>
                  </div>
                </MotionReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INVESTMENT FOCUS */}
      <section id="investment" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-14">
            <Eyebrow>Investment Focus</Eyebrow>
            <h2 className="font-serif text-3xl md:text-5xl font-bold leading-tight mt-5 mb-6 text-[#1a365d]">
              {investmentFocus.title}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">{investmentFocus.description}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {investmentFocus.features.map((feature, index) => {
              const Icon = FOCUS_ICONS[feature.icon] || Shield;
              return (
                <MotionReveal key={feature.title} delay={index * 80}>
                  <div className="group h-full bg-white hover:bg-[#f6f9fb] border border-gray-200 hover:border-[#2f9e8e]/50 hover:shadow-lg rounded-xl p-6 transition-all hover:-translate-y-1">
                    <div className="w-11 h-11 rounded-lg bg-[#e8f5f3] flex items-center justify-center mb-4 group-hover:bg-[#2f9e8e]/20 transition-colors">
                      <Icon className="w-5 h-5 text-[#2f9e8e]" />
                    </div>
                    <h3 className="font-serif text-xl font-bold mb-3 text-[#1a365d]">{feature.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                </MotionReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CAPITAL FRAMEWORK */}
      <section className="py-24 px-6 bg-[#f6f9fb]">
        <div className="max-w-7xl mx-auto">
          <Eyebrow>Capital Framework</Eyebrow>
          <h2 className="font-serif text-3xl md:text-5xl font-bold leading-tight mt-5 mb-10 max-w-3xl text-[#1a365d]">
            {capitalFramework.title}
          </h2>
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            <div className="space-y-5">
              <p className="text-lg text-gray-700 leading-relaxed">{capitalFramework.description}</p>
              <p className="text-lg text-gray-700 leading-relaxed">{capitalFramework.subdescription}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 content-start">
              {capitalFramework.stats.map((stat, index) => (
                <MotionReveal key={stat.label} delay={index * 100}>
                  <div className="bg-white border border-gray-200 rounded-xl p-7 hover:border-[#2f9e8e]/40 hover:shadow-lg transition-all h-full">
                    <div className="text-4xl font-bold text-[#2f9e8e] mb-2">{stat.value}</div>
                    <div className="font-serif text-lg font-bold text-[#1a365d] mb-1">{stat.label}</div>
                    <div className="text-sm text-gray-500">{stat.sublabel}</div>
                  </div>
                </MotionReveal>
              ))}
            </div>
          </div>

          {/* Investment structure */}
          <div className="max-w-3xl mb-10">
            <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#c19a3c] mb-3">Investment Structure</div>
            <h3 className="font-serif text-2xl md:text-4xl font-bold text-[#1a365d] leading-tight mb-5">
              {investmentStructure.title}
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed">{investmentStructure.description}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {investmentStructure.items.map((item, index) => (
              <MotionReveal key={item.number} delay={index * 80}>
                <div className="bg-gradient-to-br from-[#fcf5e3] to-white border border-[#c19a3c]/30 rounded-xl p-6 h-full hover:shadow-lg transition-all hover:-translate-y-1">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#c19a3c] font-bold mb-2">{item.number}</div>
                  <h4 className="font-serif text-lg font-bold text-[#1a365d] mb-2">{item.title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </MotionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* STRATEGIC ROLE */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-6">
              <Eyebrow>Strategic Role</Eyebrow>
              <h2 className="font-serif text-3xl md:text-5xl font-bold leading-tight mt-5 mb-6 text-[#1a365d]">
                {strategicRole.title}
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">{strategicRole.description}</p>
            </div>
            <div className="lg:col-span-6 space-y-4">
              {strategicRole.benefits.map((benefit, index) => {
                const Icon = ROLE_ICONS[benefit.icon] || Clock;
                return (
                  <MotionReveal key={benefit.title} delay={index * 80}>
                    <div className="flex items-start gap-4 p-5 bg-[#f6f9fb] border border-gray-200 rounded-xl hover:border-[#2f9e8e]/40 hover:shadow-md transition-all">
                      <div className="w-11 h-11 rounded-lg bg-[#1a365d] flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#7fd6c9]" />
                      </div>
                      <div>
                        <h3 className="font-serif text-lg font-bold text-[#1a365d] mb-1">{benefit.title}</h3>
                        <p className="text-sm text-gray-600">{benefit.description}</p>
                      </div>
                    </div>
                  </MotionReveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* GOVERNANCE */}
      <section id="governance" className="py-24 px-6 bg-[#f6f9fb]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-6">
              <Eyebrow>Governance</Eyebrow>
              <h2 className="font-serif text-3xl md:text-5xl font-bold leading-tight mt-5 mb-6 text-[#1a365d]">
                {governanceSection.title}
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-5">{governanceSection.description}</p>
              <p className="text-lg text-gray-700 leading-relaxed">{governanceSection.subdescription}</p>
            </div>
            <div className="lg:col-span-6 space-y-4">
              {governanceSection.rights.map((right, index) => {
                const Icon = GOV_ICONS[right.icon] || ShieldCheck;
                return (
                  <MotionReveal key={right.title} delay={index * 80}>
                    <div className="flex items-start gap-4 p-5 bg-white border border-gray-200 rounded-xl hover:border-[#2f9e8e]/40 hover:shadow-md transition-all">
                      <div className="w-11 h-11 rounded-lg bg-[#e8f5f3] flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#2f9e8e]" />
                      </div>
                      <div>
                        <h3 className="font-serif text-lg font-bold text-[#1a365d] mb-1">{right.title}</h3>
                        <p className="text-sm text-gray-600">{right.description}</p>
                      </div>
                    </div>
                  </MotionReveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CAPITAL NETWORK */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <Eyebrow>Capital Network</Eyebrow>
          <h2 className="font-serif text-3xl md:text-5xl font-bold leading-tight mt-5 mb-10 max-w-4xl text-[#1a365d]">
            {capitalNetwork.title}
          </h2>
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-5">
              <p className="text-lg text-gray-700 leading-relaxed">{capitalNetwork.description}</p>
              <p className="text-lg text-gray-700 leading-relaxed">{capitalNetwork.subdescription}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 content-start">
              {capitalNetwork.partners.map((partner, index) => {
                const Icon = NETWORK_ICONS[partner.icon] || Landmark;
                return (
                  <MotionReveal key={partner.title} delay={index * 100}>
                    <div className="group bg-[#f6f9fb] hover:bg-white border border-gray-200 hover:border-[#2f9e8e]/40 hover:shadow-lg rounded-xl p-6 transition-all hover:-translate-y-1 h-full">
                      <div className="w-11 h-11 rounded-lg bg-[#e8f5f3] flex items-center justify-center mb-4 group-hover:bg-[#2f9e8e]/20 transition-colors">
                        <Icon className="w-5 h-5 text-[#2f9e8e]" />
                      </div>
                      <h3 className="font-serif text-lg font-bold text-[#1a365d] mb-2">{partner.title}</h3>
                      <p className="text-sm text-gray-600">{partner.description}</p>
                    </div>
                  </MotionReveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* WHY PARTNER */}
      <section id="partnership" className="py-24 px-6 bg-[#f6f9fb]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-14">
            <Eyebrow>Why Partner With Us</Eyebrow>
            <h2 className="font-serif text-3xl md:text-5xl font-bold leading-tight mt-5 mb-6 text-[#1a365d]">
              {whyPartner.title}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">{whyPartner.intro}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {whyPartner.reasons.map((reason, index) => (
              <MotionReveal key={reason.number} delay={index * 60}>
                <div className="group h-full bg-white border border-gray-200 hover:border-[#2f9e8e]/50 hover:shadow-lg rounded-xl p-6 transition-all hover:-translate-y-1">
                  <div className="w-9 h-9 rounded-full bg-[#e8f5f3] border border-[#2f9e8e]/40 flex items-center justify-center text-[#2f9e8e] text-sm font-bold mb-5 group-hover:bg-[#2f9e8e] group-hover:text-white transition-colors">
                    {reason.number}
                  </div>
                  <h3 className="font-serif text-xl font-bold text-[#1a365d] mb-3">{reason.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{reason.description}</p>
                </div>
              </MotionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <Eyebrow>The Bottom Line</Eyebrow>
          <h2 className="font-serif text-3xl md:text-5xl font-bold leading-tight mt-5 mb-10 max-w-4xl text-[#1a365d]">
            {closingSection.title}
          </h2>

          <MotionReveal className="mb-12">
            <div className="relative bg-[#f6f9fb] border border-gray-200 border-l-4 border-l-[#2f9e8e] rounded-r-xl p-8 max-w-4xl">
              <Quote className="absolute top-6 right-6 w-8 h-8 text-[#2f9e8e]/20" />
              <p className="text-gray-700 text-lg md:text-xl leading-relaxed italic">
                <span className="font-bold not-italic text-[#1a365d]">GPs do not resist BIG</span> because we don't compete with them, constrain them, second-guess them, destabilize their LP base, disappear in downturns, or extract value at their expense.
              </p>
            </div>
          </MotionReveal>

          <p className="text-lg text-gray-700 leading-relaxed mb-12 max-w-4xl">
            {closingSection.description}
          </p>

          <div className="grid md:grid-cols-3 gap-4 mb-14">
            {closingSection.features.map((feature, index) => {
              const Icon = CLOSING_ICONS[feature.icon] || CheckCircle;
              return (
                <MotionReveal key={feature.title} delay={index * 80}>
                  <div className="bg-[#f6f9fb] border border-gray-200 rounded-xl p-6 h-full hover:border-[#2f9e8e]/40 hover:shadow-lg transition-all hover:-translate-y-1">
                    <div className="w-11 h-11 rounded-lg bg-[#e8f5f3] flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-[#2f9e8e]" />
                    </div>
                    <h3 className="font-serif text-lg font-bold text-[#1a365d] mb-2">{feature.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                </MotionReveal>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-3">
            <button onClick={handleStartConversation}
              className="group inline-flex items-center gap-2 bg-[#2f9e8e] hover:bg-[#268578] text-white px-7 py-4 rounded-lg font-semibold transition-all hover:shadow-xl hover:-translate-y-0.5"
              data-testid="institutional-cta-conversation">
              Start a Conversation <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={handleSubmitProposal}
              className="group inline-flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-300 hover:border-[#1a365d] text-[#1a365d] px-7 py-4 rounded-lg font-semibold transition-all"
              data-testid="institutional-cta-proposal">
              Submit Investment Proposal <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      <Footer />

      <PartnerModal isOpen={isPartnerModalOpen} onClose={() => setIsPartnerModalOpen(false)} />
      <InvestmentProposalModal isOpen={isProposalModalOpen} onClose={() => setIsProposalModalOpen(false)} />
    </div>
  );
};

export default InstitutionalPage;
